import { deserialize } from 'flatgeobuf/lib/mjs/geojson';
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { getCsrfToken } from './arcgisProxy';
import { dedupeInFlight } from './requestDedupe';

interface DateFilterOptions {
  dateField?: string;
  from?: Date;
  to?: Date;
}

interface FilterOptions extends DateFilterOptions {
  filterField?: string;
  filterValue?: string;
}

/**
 * Named geometry resolution to serve, resolved server-side against a per-table
 * allowlist and rejected if that table does not offer it -- counties offer all
 * five, the perimeter tables full and simplified. Omitting it takes the table's
 * default. Each generalized variant is faithful only up to the zoom its tolerance
 * justifies, which apps/web/src/map/geometryDetail.ts derives per camera.
 */
export type GeometryVariant = 'full' | 'finer' | 'fine' | 'simplified' | 'coarse';

interface FetchOptions extends FilterOptions {
  fields?: string[];
  geometry?: GeometryVariant;
  excludeGeometry?: boolean;
  distinct?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  bbox?: [number, number, number, number];
  /**
   * Aborting cancels the HTTP request, which the server propagates to its
   * in-flight Postgres query -- callers superseding a stale request (e.g. the
   * time slider) free the server, not just the client.
   */
  signal?: AbortSignal;
}

function buildParams(options?: FetchOptions): URLSearchParams {
  const params = new URLSearchParams();
  if (options?.dateField) params.set('dateField', options.dateField);
  if (options?.from) params.set('from', options.from.toISOString());
  if (options?.to) params.set('to', options.to.toISOString());
  if (options?.fields?.length) params.set('fields', options.fields.join(','));
  if (options?.geometry) params.set('geometry', options.geometry);
  if (options?.excludeGeometry) params.set('excludeGeometry', 'true');
  if (options?.distinct) params.set('distinct', 'true');
  // != null, not truthiness: offset 0 must reach the server -- it marks the
  // request as page 1 of a paginated read, which the server orders by ctid so
  // every page slices one total order. Omitting offset entirely requests a
  // single unordered page (fast path, no top-N sort).
  if (options?.limit != null) params.set('limit', String(options.limit));
  if (options?.offset != null) params.set('offset', String(options.offset));
  if (options?.orderBy) params.set('orderBy', options.orderBy);
  if (options?.orderDirection) params.set('orderDirection', options.orderDirection);
  if (options?.bbox) params.set('bbox', options.bbox.join(','));
  // != null, not truthiness: an empty filterValue is a real filter (column =
  // '') and must reach the server, not silently request the whole table.
  if (options?.filterField != null && options?.filterValue != null) {
    params.set('filterField', options.filterField);
    params.set('filterValue', options.filterValue);
  }
  return params;
}

// Page size for requests against GET /api/features/:table. Must not exceed
// the server's cap (FEATURES_MAX_PAGE_SIZE in
// apps/server/src/features/features.service.ts), or the short-page check in
// fetchFlatGeobuf would end paging early and drop features.
export const FEATURES_PAGE_SIZE = 10000;

async function fetchFlatGeobufPage<
  G extends Geometry = Geometry,
  P extends GeoJsonProperties = GeoJsonProperties,
>(table: string, options?: FetchOptions): Promise<FeatureCollection<G, P>> {
  const params = buildParams(options);
  const url = `/api/features/${table}?${params}`;

  const response = await fetch(url, { credentials: 'include', signal: options?.signal });
  if (!response.ok) {
    throw new Error(`Features API error: ${response.status}`);
  }

  const buffer = await response.arrayBuffer();
  if (buffer.byteLength === 0) {
    return { type: 'FeatureCollection', features: [] };
  }

  const features: Feature<G, P>[] = [];
  for await (const feature of deserialize(new Uint8Array(buffer))) {
    features.push(feature as Feature<G, P>);
  }
  return { type: 'FeatureCollection', features };
}

export async function fetchFlatGeobuf<
  G extends Geometry = Geometry,
  P extends GeoJsonProperties = GeoJsonProperties,
>(table: string, options?: FetchOptions): Promise<FeatureCollection<G, P>> {
  // Callers that manage pagination themselves get exactly the page they asked
  // for. The server caps pages at its max page size either way.
  if (options?.limit != null || options?.offset != null) {
    return fetchFlatGeobufPage(table, options);
  }

  // The server applies a default limit when none is given, so fetch pages
  // until a short page signals the end of the result set.
  const features: Feature<G, P>[] = [];
  let offset = 0;
  for (;;) {
    const page = await fetchFlatGeobufPage<G, P>(table, {
      ...options,
      limit: FEATURES_PAGE_SIZE,
      offset,
    });
    features.push(...page.features);
    if (page.features.length < FEATURES_PAGE_SIZE) {
      return { type: 'FeatureCollection', features };
    }
    offset += page.features.length;
  }
}

interface CountOptions extends FilterOptions {
  signal?: AbortSignal;
}

export async function fetchFeatureCount(table: string, options?: CountOptions): Promise<number> {
  const params = new URLSearchParams();
  if (options?.dateField) params.set('dateField', options.dateField);
  if (options?.from) params.set('from', options.from.toISOString());
  if (options?.to) params.set('to', options.to.toISOString());
  if (options?.filterField != null && options?.filterValue != null) {
    params.set('filterField', options.filterField);
    params.set('filterValue', options.filterValue);
  }

  const response = await fetch(`/api/features/${table}/count?${params}`, {
    credentials: 'include',
    signal: options?.signal,
  });
  if (!response.ok) {
    throw new Error(`Features API error: ${response.status}`);
  }

  const data: { count: number } = await response.json();
  return data.count;
}

interface StatsOptions extends FilterOptions {
  groupBy: string[];
  /** Comma-separated `type:field` definitions, e.g. `count:kml_id`. */
  statistics: string;
  bbox?: [number, number, number, number];
  /**
   * Restrict the aggregation to these counties, as the "<county>, <state>"
   * strings the incident views hold, and/or to these states. The server applies
   * whichever of the two columns the table has (county where available, state
   * otherwise), so both can be sent together.
   */
  counties?: string[];
  states?: string[];
}

export type FeatureRow = Record<string, string | number | null>;

// The stats and event-detail endpoints answer with one JSON object per row.
// Validated rather than cast, because the cast made the shape a render-time
// problem: a chart-dot click white-screened the app with a TypeError reading
// 'lastupdatedon' of null, so the list had been handed something other than the
// rows its type claimed. Rejecting here turns that into a fetch failure the
// callers already handle. Values are checked too, not just the row shape, since
// callers pass them to new Date(), Number() and chart datasets -- and the features
// schema holds one boolean column no client requests, so nothing real is rejected.
function parseFeatureRows(body: unknown, endpoint: string): FeatureRow[] {
  if (!Array.isArray(body)) {
    throw new Error(`Features API ${endpoint} response is not an array`);
  }
  for (const row of body) {
    if (row === null || typeof row !== 'object' || Array.isArray(row)) {
      throw new Error(`Features API ${endpoint} response contains a non-object row`);
    }
    for (const [column, value] of Object.entries(row as Record<string, unknown>)) {
      if (value !== null && typeof value !== 'string' && typeof value !== 'number') {
        throw new Error(
          `Features API ${endpoint} response has a ${Array.isArray(value) ? 'array' : typeof value} value for "${column}"`
        );
      }
    }
  }
  return body as FeatureRow[];
}

// Server-side GROUP BY aggregation: one row per group, so the payload is the
// buckets a chart draws rather than every underlying feature. Concurrent requests
// for the same aggregation share one response, since a view load asks for the same
// numbers from the tiles, the chart and the availability probe at once. Coalescing
// only, so the periodic refresh still reaches the server.
export async function fetchFeatureStats(
  table: string,
  options: StatsOptions
): Promise<FeatureRow[]> {
  const params = new URLSearchParams({
    groupBy: options.groupBy.join(','),
    statistics: options.statistics,
  });
  if (options.dateField) params.set('dateField', options.dateField);
  if (options.from) params.set('from', options.from.toISOString());
  if (options.to) params.set('to', options.to.toISOString());
  if (options.filterField != null && options.filterValue != null) {
    params.set('filterField', options.filterField);
    params.set('filterValue', options.filterValue);
  }
  if (options.bbox) params.set('bbox', options.bbox.join(','));
  // JSON-encoded so county names (which contain commas) survive unambiguously.
  if (options.counties?.length) params.set('counties', JSON.stringify(options.counties));
  if (options.states?.length) params.set('states', JSON.stringify(options.states));

  const url = `/api/features/${table}/stats?${params}`;

  return dedupeInFlight(url, async () => {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      throw new Error(`Features API error: ${response.status}`);
    }

    return parseFeatureRows(await response.json(), `${table}/stats`);
  });
}

export type EventDetailFilter =
  | { field: string; value: string }
  | { field: string; isNull: true }
  | { field: string; in: string[]; includeNull?: boolean };

interface EventDetailOptions {
  /** Column to DISTINCT ON, i.e. one returned row per group (e.g. county). */
  distinctOn: string;
  /** Column whose DESC order picks the "latest" row per group. */
  orderBy: string;
  filters: EventDetailFilter[];
  fields: string[];
  signal?: AbortSignal;
}

// The latest row per group behind clicked chart dot(s), via
// POST /api/features/:table/event-detail. One indexed query returns exactly the
// clicked entities' rows -- no over-fetching or client-side filtering. POST
// because an `in` filter can carry hundreds of entity ids (e.g. every Ting
// outage in a chart bucket), which as a GET query string exceeds CloudFront's
// URL length cap and 414s before reaching the server.
export async function fetchEventDetail(
  table: string,
  options: EventDetailOptions
): Promise<FeatureRow[]> {
  const response = await fetch(`/api/features/${table}/event-detail`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getCsrfToken() },
    body: JSON.stringify({
      distinctOn: options.distinctOn,
      orderBy: options.orderBy,
      filters: options.filters,
      fields: options.fields,
    }),
    signal: options.signal,
  });
  if (!response.ok) {
    throw new Error(`Features API error: ${response.status}`);
  }

  return parseFeatureRows(await response.json(), `${table}/event-detail`);
}

// Distinct values of a timestamp column within [from, to], as ascending ISO
// strings. Each workflow run stamps its whole batch with one timestamp, so these
// are the snapshot times available to step through. The window is required and
// the server caps its span: it answers a day-sized window from an index scan
// instead of deduping every row since `from` across the full (and growing)
// _history table.
export async function fetchSnapshotTimes(
  table: string,
  dateField: string,
  from: Date,
  to: Date,
  signal?: AbortSignal
): Promise<string[]> {
  const params = new URLSearchParams({
    dateField,
    from: from.toISOString(),
    to: to.toISOString(),
  });

  const response = await fetch(`/api/features/${table}/dates?${params}`, {
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    throw new Error(`Features API error: ${response.status}`);
  }

  return response.json();
}
